import { useHelpDictionary } from '@/shared/api';
import {
  Card,
  Loader,
  ModalComponentWrapper,
  UserProfileAction,
} from '@/shared/components';
import { useContactUs, useModalStore, useZohoChat } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import HeadphonesIcon from '@/shared/assets/headphones-icon.svg?react';
import DocumentIcon from '@/shared/assets/document-icon.svg?react';
import EmailIcon from '@/shared/assets/email-alt-icon.svg?react';

type Props = {
  onBack: () => void;
  ewalletAccNum: string | undefined;
};

const classes = tv({
  slots: {
    content: 'flex flex-col gap-[2.4rem]',
    card: 'p-[0.8rem] rounded-[1.6rem] w-full shadow-none flex flex-col gap-2',
    icon: 'size-10 text-primary',
    button: 'px-[2.4rem] min-h-[5.6rem] w-full',
    link: 'flex items-center justify-start gap-[1.6rem]',
    text: 'text-primary text-[16px] font-[500] whitespace-normal',
  },
});

export function HelpAndSupportModal({ onBack, ewalletAccNum }: Props) {
  const { t } = useTranslation();
  const c = classes();

  const { closeDialog } = useModalStore();
  const { helpData, isHelpLoading, helpError } = useHelpDictionary();
  const { links } = useContactUs({ ewalletAccNum });
  const { open: openZohoChat } = useZohoChat();

  if (isHelpLoading || !helpData) {
    return <Loader />;
  }

  if (helpError) {
    return <div>Error</div>;
  }

  const handleContactUsClick = () => {
    window.open(links?.defaultRequest || '', '_blank');
  };

  const handleOnlineChatClick = () => {
    openZohoChat();
    closeDialog();
  };

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.helpAndSupport.title')}
      onBack={onBack}
      headerClassName="text-secondary-bg bg-secondary-bg/50">
      <div className={c.content()}>
        <Card className={c.card()}>
          <UserProfileAction
            icon={<HeadphonesIcon className={c.icon()} />}
            title={t('personalAccount.userProfile.helpAndSupport.onlineChat')}
            onClick={handleOnlineChatClick}
          />

          <UserProfileAction
            icon={<EmailIcon className={c.icon()} />}
            title={t('personalAccount.userProfile.helpAndSupport.contactUs')}
            onClick={handleContactUsClick}
          />
        </Card>

        <Card className={c.card()}>
          {helpData.value.map((docObj, index) => {
            const [name, url] = Object.entries(docObj)[0];

            return (
              <UserProfileAction
                key={`${name}-${index}`}
                icon={<DocumentIcon className={c.icon()} />}
                title={name}
                onClick={() => window.open(url, '_blank')}
              />
            );
          })}
        </Card>
      </div>
    </ModalComponentWrapper>
  );
}
